import { usePathname } from 'expo-router';
import { useEffect } from 'react';
import { Platform } from 'react-native';
import { useAuth } from '@/lib/AuthContext';
import { isAnonAllowedPath } from '@/lib/anonAccess';
import { supabase } from '@/lib/supabase';
import { redirectToWordPressLogin } from '@/lib/wordpress-redirect';
import { useSSOTokenExchange } from '@/hooks/useSSOTokenExchange';

// Debounce protects against the brief `!user && !sso_token` window right after
// a successful SSO exchange, which otherwise bounces us back to WordPress.
const WORDPRESS_LOGIN_REDIRECT_DELAY_MS = 400;

/** Single source of truth for redirecting unauthenticated web users to WordPress. */
export function useWordPressLoginRedirect(): void {
  const { user, loading } = useAuth();
  const { ssoToken, ssoProcessing, ssoError } = useSSOTokenExchange();
  // Track pathname so client-side navigation (e.g. anon-allowed route →
  // non-allowed route) re-evaluates this effect. Without it, an anon user
  // navigating away from a permitted course page would stay put because
  // none of the auth deps changed.
  const pathname = usePathname();

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const isAuthResolving = loading || ssoProcessing || !!ssoToken || !!ssoError;

    // Anonymous Supabase users count as "not effectively logged in" for routing
    // purposes — they only belong on anon-allowed routes.
    const isAnonUser = !!user?.is_anonymous;
    const hasRealUser = !!user && !isAnonUser;

    if (hasRealUser || isAuthResolving) return;

    const onAllowedRoute = isAnonAllowedPath(pathname);

    if (onAllowedRoute) {
      // Either AuthContext is creating an anon session (no user yet) or the anon
      // session is already established — stay put either way.
      return;
    }

    // Off-allowlist: if we somehow have an anon session here, drop it before
    // bouncing so the WP→Supabase SSO exchange isn't confused by a stale anon row.
    if (isAnonUser) {
      void supabase.auth.signOut();
    }

    const timer = setTimeout(redirectToWordPressLogin, WORDPRESS_LOGIN_REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [user, loading, ssoProcessing, ssoToken, ssoError, pathname]);
}
